import React, { useCallback, useEffect, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { useHotkeys } from 'react-hotkeys-hook';

import { apiDelete, apiPatch } from '../../lib/api_v2';
import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { logError, logFatal } from '../../lib/logger';

import SpinnerIcon from '../../../assets/images/spinner.svg';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import { responseValidations } from '../settings/account/accountStyles';
import { SETTINGS_MODAL_CLOSE_BUTTON_CLASS, SETTINGS_MODAL_CLOSE_ICON, SETTINGS_MODAL_SAVE_PILL_CLASS, SETTINGS_MODAL_TITLE_CLASS } from '../shared/settingsModalChrome';
import { Modal } from './Modal';
import { ModalBody } from './ModalBody';
import { ModalContent } from './ModalContent';
import { useModalContext } from './ModalPortal';
import { ModalTopBar } from './ModalTopBar';

// A deliberately simple settings modal: rename the breakout, or delete it.
function ChannelSettings({ channelId, onClose }) {
  const { unlocked } = useModalContext();
  const [errors, setErrors] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { workspaceId, profile: currentProfile } = useRouteWorkspace();
  const channel = useLiveQuery(
    function fetchChannel() {
      if (!channelId) return null;
      return db.channels.get([workspaceId, channelId]);
    },
    [channelId, workspaceId],
    DEXIE_IS_LOADING,
  );

  const isAdmin = currentProfile?.role === 'admin';
  const channelName = channel && channel !== DEXIE_IS_LOADING ? channel.name || '' : '';
  const trimmedName = name.trim();
  const isDirty = trimmedName.length > 0 && trimmedName !== channelName;

  // Initialize form data when channel loads
  useEffect(
    function initializeFormData() {
      if (channel && channel !== DEXIE_IS_LOADING) {
        setName(channel.name || '');
        setIsDataLoaded(true);
      }
    },
    [channel],
  );

  const handleNameChange = useCallback((event) => {
    setName(event.target.value);
    setErrors((prev) => ({ ...prev, name: null, general: null }));
  }, []);

  const handleDeleteChannel = useCallback(
    async function handleDeleteChannelCallback() {
      if (!window.confirm('Are you sure you want to delete this breakout?')) return;
      setIsDeleting(true);
      setErrors({});

      try {
        const response = await apiDelete(`/channels/${channelId}`);
        const data = await response.json();

        if (data.ok) {
          window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: {} }));
          if (onClose) onClose();
        }
      } catch (error) {
        logError('ChannelSettings', 'Could not delete channel', { error });
        setErrors({ general: 'An error occurred while deleting the breakout. Please try again.' });
      } finally {
        setIsDeleting(false);
      }
    },
    [channelId, onClose],
  );

  const handleSave = useCallback(
    async function handleSaveCallback() {
      if (!isDirty || isSubmitting) return;

      setIsSubmitting(true);
      setErrors({});

      try {
        const response = await apiPatch(`/channels/${channelId}`, { channel: { name: trimmedName } });
        const data = await response.json();

        if (data.ok) {
          // Closing the modal doubles as the success feedback, matching the settings pages
          if (onClose) onClose();
        } else {
          const validation_errors = responseValidations(data);
          setErrors(validation_errors || { general: 'Please check your input and try again.' });
        }
      } catch (error) {
        logError('ChannelSettings', 'Could not update channel', { error });
        setErrors({ general: 'An error occurred. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [channelId, isDirty, isSubmitting, onClose, trimmedName],
  );

  const handleEnterKey = useCallback(
    function handleEnterKeyCallback() {
      if (unlocked()) {
        handleSave();
      }
    },
    [unlocked, handleSave],
  );

  const handleFormSubmit = useCallback(
    function handleFormSubmitCallback(e) {
      e.preventDefault();
      handleSave();
    },
    [handleSave],
  );

  const hotkeyRef = useHotkeys('enter', handleEnterKey, { preventDefault: true }, [handleEnterKey]);

  if (!channelId) {
    logFatal('ChannelSettings', 'Missing channelId for settings modal', { channelId });
    return null;
  }

  const modalChromeProps = {
    className: 'rounded-2xl',
    closePlacement: 'top-left',
    showClose: { desktop: true, mobile: true },
    closeButtonClassName: SETTINGS_MODAL_CLOSE_BUTTON_CLASS,
    closeIcon: SETTINGS_MODAL_CLOSE_ICON,
  };

  if (channel === DEXIE_IS_LOADING) {
    return (
      <Modal onClose={onClose} ref={hotkeyRef} {...modalChromeProps}>
        <ModalBody className="flex min-h-40 items-center justify-center p-8">
          <SpinnerIcon className="size-8 animate-spin text-gray-500 dark:text-neutral-400" />
        </ModalBody>
      </Modal>
    );
  }

  if (!channel) {
    logError('ChannelSettings', 'Channel not found in local database', { channelId });
    return null;
  }

  // sanity check, only open_breakouts are editable
  if (channel.kind !== 'open_breakout') {
    logFatal('ChannelSettings', 'Invalid channel kind for settings modal', { channelId, channel });
    return null;
  }

  const nameErrors = Array.isArray(errors.name) ? errors.name.join(', ') : errors.name;

  return (
    <Modal onClose={onClose} ref={hotkeyRef} {...modalChromeProps}>
      <ModalBody className="h-full">
        <ModalTopBar className="!pb-0">
          <span className={SETTINGS_MODAL_TITLE_CLASS}>{channel.name}</span>
        </ModalTopBar>
        {isDataLoaded && (
          <ModalContent className="min-h-0 md:!px-5">
            <form onSubmit={handleFormSubmit} className="w-full pt-6">
              {/* Sits in the modal top bar, opposite the close button — same pill contract as the settings pages */}
              <button
                type="submit"
                data-app-region="none"
                disabled={isSubmitting || !isDirty}
                className={SETTINGS_MODAL_SAVE_PILL_CLASS}
              >
                {isSubmitting ? '...' : 'Save'}
              </button>

              {errors.general && (
                <div className="mb-5 rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">
                  {errors.general}
                </div>
              )}

              <label htmlFor="channel_name" className="block text-cursed text-black dark:text-neutral-100">
                Channel name
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  id="channel_name"
                  name="channel_name"
                  value={name}
                  onChange={handleNameChange}
                  required
                  autoFocus
                  maxLength={80}
                  autoComplete="off"
                  data-1p-ignore="true"
                  className="h-12 w-full rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 text-[15px] leading-5 text-black placeholder:text-gray-400 hover:bg-white focus:outline-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500"
                />
              </div>
              {nameErrors && <span className="mt-1 block text-left text-xs text-red-400">{nameErrors}</span>}
            </form>

            {isAdmin && (
              <div className="mt-8 flex w-full flex-col items-start gap-3 rounded-lg border-[0.5px] border-red-200 bg-red-50 p-4 dark:border-red-500/20 dark:bg-red-950/30">
                <div className="flex flex-col gap-1">
                  <span className="text-cursed font-medium text-red-600 dark:text-red-400">Delete Breakout</span>
                  <p className="text-cursed text-red-900/70 dark:text-red-200/70">
                    Deleting this breakout permanently deletes every message in it. This cannot be undone.
                  </p>
                </div>
                <button
                  type="button"
                  data-app-region="none"
                  onClick={handleDeleteChannel}
                  disabled={isDeleting}
                  className="flex h-9 cursor-pointer items-center justify-center rounded-full bg-red-500 px-4 text-sm leading-[18px] font-medium text-white hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Breakout'}
                </button>
              </div>
            )}
          </ModalContent>
        )}
      </ModalBody>
    </Modal>
  );
}

export default ChannelSettings;
